import { useTranslation } from 'react-i18next';
import { NumberInput, Stack, Switch, Text } from '@mantine/core';
import { decodeEntities } from '@/lib/format';
import type { QaOverrides } from '@/lib/buyAcceptance';
import type { QualityAcceptanceProperty } from '@/types/buy';
import classes from './BuyAcceptanceFields.module.css';

interface BuyAcceptanceFieldsProps {
  /** Свойства приёмки культуры (qaDefaults: имя + ГОСТ-значение). */
  readonly qaProps: readonly QualityAcceptanceProperty[];
  readonly byGost: boolean;
  readonly onByGost: (value: boolean) => void;
  readonly overrides: QaOverrides;
  readonly onOverride: (name: string, field: 'inc' | 'dec', value: number | string) => void;
  /** qaDefaults ещё грузится — переключатель недоступен. */
  readonly loading?: boolean;
  /** Свойства загрузились и их нет — режим «не по ГОСТу» невозможен (бэкенд требует непустой список). */
  readonly noProps?: boolean;
  /**
   * Отмечать пустые правила ошибкой. Форма прайс-листа передаёт признак попытки отправки, чтобы нетронутая форма не
   * была красной; по умолчанию пустое отмечается сразу.
   */
  readonly showEmptyErrors?: boolean;
  /** Идентификаторы полей правил: по ним сводка ошибок формы ведёт к пустому полю. */
  readonly fieldId?: (name: string, field: 'inc' | 'dec') => string;
}

// Приёмка по сорности/влажности/примесям: переключатель «Только по ГОСТу» + либо значения ГОСТа строкой, либо
// по каждому свойству правило «Если {параметр} превышает [N%] → снижение веса на [M%]». Контролируемый
// компонент — единый для формы прайса и сделки «Купить» из свободного поиска (зачёт качества НЕ здесь:
// в сделку он не входит, живёт только в прайсе).
export default function BuyAcceptanceFields({
  qaProps,
  byGost,
  onByGost,
  overrides,
  onOverride,
  loading,
  noProps,
  showEmptyErrors = true,
  fieldId,
}: BuyAcceptanceFieldsProps) {
  const { t } = useTranslation();
  return (
    <Stack gap="xs">
      <Text fw={600}>{t('app.buy.terms.acceptance')}</Text>
      <Switch
        checked={byGost}
        onChange={(event) => onByGost(event.currentTarget.checked)}
        label={t('app.buy.terms.acceptanceGost')}
        disabled={loading || noProps}
      />
      {noProps ? (
        <Text size="sm" c="gray.7">
          {t('app.buy.form.gostOnly')}
        </Text>
      ) : null}
      {byGost ? (
        qaProps.length > 0 ? (
          <Text size="sm">
            {qaProps
              .map((property) => (property.gostValue ? `${property.name} ${decodeEntities(property.gostValue)}` : property.name))
              .join(' · ')}
          </Text>
        ) : null
      ) : qaProps.length > 0 ? (
        <Stack gap="xs">
          {qaProps.map((property) => {
            const override = overrides[property.name] ?? { inc: '', dec: '' };
            return (
              // На широком экране правило — одна строка; на телефоне — текст правила и под ним два поля с подписями,
              // чтобы «снижение веса» не отрывалось от своего поля.
              <div key={property.name} className={classes.rule}>
                <Text size="sm" className={classes.ruleText}>
                  {t('app.buy.form.qaRuleIf')}{' '}
                  <Text component="span" fw={600}>
                    {property.name}
                  </Text>{' '}
                  {t('app.buy.form.qaRuleExceeds')}
                </Text>
                <NumberInput
                  id={fieldId?.(property.name, 'inc')}
                  value={override.inc}
                  onChange={(value) => onOverride(property.name, 'inc', value)}
                  min={0}
                  allowNegative={false}
                  decimalScale={1}
                  suffix=" %"
                  hideControls
                  className={classes.field}
                  label={t('app.buy.form.qaLimit')}
                  classNames={{ label: classes.narrowLabel }}
                  error={showEmptyErrors && override.inc === '' ? t('auth.validation.required') : undefined}
                  aria-label={`${property.name}: ${t('app.buy.form.qaLimit')}`}
                />
                <Text size="sm" className={classes.then}>
                  → {t('app.buy.form.qaRuleThen')}
                </Text>
                <NumberInput
                  id={fieldId?.(property.name, 'dec')}
                  value={override.dec}
                  onChange={(value) => onOverride(property.name, 'dec', value)}
                  min={0}
                  allowNegative={false}
                  decimalScale={1}
                  suffix=" %"
                  hideControls
                  className={classes.field}
                  label={t('app.buy.form.qaDecrease')}
                  classNames={{ label: classes.narrowLabel }}
                  error={showEmptyErrors && override.dec === '' ? t('auth.validation.required') : undefined}
                  aria-label={`${property.name}: ${t('app.buy.form.qaDecrease')}`}
                />
              </div>
            );
          })}
        </Stack>
      ) : null}
    </Stack>
  );
}
